import { EntityType, useAppSelector } from '@frontend/common';
import { Logger } from '@frontend/logger';
import { MqttBroker } from '@frontend/pub-sub';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAccountRepository } from '../account/account-repository';
import { buildEntityMqttTopic, resolveEntityMqttAccountId } from '../mqtt/entity-mqtt-topic';
import { awaitResolve } from '../resolve/await-resolve';
import { ResolveFunction, getResolveFunction } from '../resolve/resolve-function-registry';
import { getResolvedEntity, resolveStore } from '../resolve/resolve-slice';
import { markRowPendingDelete, patchRow, removeRow } from './list-slice';

/** How long a deleted row stays visible, greyed out, before it disappears. */
export const DELETED_ROW_GRACE_PERIOD_MS = 2000;

export interface UseReactiveRowProps {
    listId: string;
    entityType: EntityType;
    id?: string;
    /** Account that owns this row's entity. Falls back to currentAccount when omitted. */
    accountId?: string;
    dispatch: ThunkDispatch<any, any, Action>;
    /**
     * Overrides the resolve function registered for `entityType`. Needed for lists whose entity
     * type is shared by several resolvers, such as the group member types.
     */
    resolveFunction?: ResolveFunction;
    /**
     * Called when an update arrives that we cannot turn into fresh row data — no payload on the
     * event and no resolver for this entity type. The list falls back to prompting a refresh.
     */
    onUnresolvableUpdate?: () => void;
}

/**
 * Keeps a single list row in sync with its entity.
 *
 * This is the `useReactiveObject` pattern applied to a row: same per-entity MQTT topic and same
 * resolve cache, but seeded from the row the list already fetched instead of resolving on mount,
 * so opening a list does not trigger a resolve call for every visible row. Updates are merged into
 * the row rather than replacing it, because list endpoints often return a richer shape than the
 * resolve endpoint does.
 */
export function useReactiveRow({ listId, entityType, id, accountId, dispatch, resolveFunction, onUnresolvableUpdate }: UseReactiveRowProps): void {
    // Stable per hook instance so two rows can never share a broker subscription slot.
    const [subscriberId] = useState<string>(() => `reactive-row:${crypto.randomUUID()}`);
    const { currentAccount } = useAccountRepository();
    const resolveState = useAppSelector(useSelector, resolveStore);
    const resolveStateRef = useRef(resolveState);
    resolveStateRef.current = resolveState;
    const mqttAccountId = resolveEntityMqttAccountId(accountId, currentAccount);

    const onUnresolvableUpdateRef = useRef(onUnresolvableUpdate);
    onUnresolvableUpdateRef.current = onUnresolvableUpdate;

    useEffect(() => {
        if (!id || !mqttAccountId) return;

        const removalTimeouts: ReturnType<typeof setTimeout>[] = [];
        const topic = buildEntityMqttTopic(mqttAccountId, entityType, id);

        const applyUpdate = async (data: { id: string } | null) => {
            if (data && data.id === id) {
                dispatch(patchRow({ listId, row: data }));
                return;
            }
            const resolve = resolveFunction ?? getResolveFunction(entityType);
            if (!resolve) {
                Logger.warn('Reactive row cannot resolve its entity, falling back to a manual refresh', { entityType, id });
                onUnresolvableUpdateRef.current?.();
                return;
            }
            await awaitResolve(id, resolve);
            const resolved = getResolvedEntity<{ id: string }>(resolveStateRef.current, entityType, id);
            if (resolved) dispatch(patchRow({ listId, row: resolved }));
            else onUnresolvableUpdateRef.current?.();
        };

        MqttBroker.getInstance().subscribe(subscriberId, topic, (message: string, data: { id: string } | null) => {
            if (message.includes('deleted')) {
                dispatch(markRowPendingDelete({ listId, id }));
                removalTimeouts.push(setTimeout(() => dispatch(removeRow({ listId, id })), DELETED_ROW_GRACE_PERIOD_MS));
            } else if (message.includes('updated')) {
                void applyUpdate(data);
            }
        });

        return () => {
            removalTimeouts.forEach(clearTimeout);
            MqttBroker.getInstance().unSubscribe(subscriberId, topic);
        };
    }, [id, entityType, mqttAccountId, listId, dispatch, resolveFunction, subscriberId]);
}
